
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Chip, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, InputAdornment, Grow, Stack, Divider, Card, Grid,
  Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';
import { MapContainer, TileLayer, Marker, useMapEvents, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import Autocomplete from '@mui/material/Autocomplete';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpIcon from '@mui/icons-material/Help';
import Tooltip from '@mui/material/Tooltip';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function LocationMarker({ position, setPosition }) {
  const map = useMapEvents({
    click(e) {
      if (setPosition) {
          setPosition(e.latlng);
          map.flyTo(e.latlng, map.getZoom());
      }
    },
  });

  return position ? <Marker position={position} /> : null;
}

function parseCoordinates(str) {
  if (!str || typeof str !== 'string') return null;
  const parts = str.split(',');
  if (parts.length !== 2) return null;
  const lat = parseFloat(parts[0].trim());
  const lng = parseFloat(parts[1].trim());
  if (isNaN(lat) || isNaN(lng)) return null;
  return { lat, lng };
}

function MapResizer() {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 200);
    return () => clearTimeout(timer);
  }, [map]);
  return null;
}

// Renders a location string; if it is parseable coordinates, make it clickable
// to open a map popup showing the pin.
function ClickableLocation({ locationString, label, onOpenMap }) {
  const coords = parseCoordinates(locationString);
  if (!coords) {
    return (
      <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f1f5f9', px: 1, py: 0.5, borderRadius: 1, color: '#475569' }}>
        {locationString}
      </Typography>
    );
  }
  return (
    <Tooltip title={`View ${label} location on map`}>
      <Typography
        component="span"
        onClick={() => onOpenMap(coords, label)}
        sx={{
          fontFamily: 'monospace', bgcolor: '#eff6ff', px: 1, py: 0.5, borderRadius: 1, color: '#2563eb',
          cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5,
          border: '1px solid #bfdbfe',
          '&:hover': { bgcolor: '#dbeafe', borderColor: '#93c5fd' }
        }}
      >
        <LocationOnIcon sx={{ fontSize: 14 }} />
        {locationString}
      </Typography>
    </Tooltip>
  );
}

function PunchingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  
  // Edit State
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({
      id: null,
      date: '',
      check_in: '',
      check_in_location: '',
      use_assigned_in: false,
      selectedAssignedIn: '',
      check_out: '',
      check_out_location: '',
      use_assigned_out: false,
      selectedAssignedOut: ''
  });
  const [addPunchOpen, setAddPunchOpen] = useState(false);
  const [addPunchData, setAddPunchData] = useState({ date: '', check_in: '', check_in_location: '', use_assigned_in: false, selectedAssignedIn: '', check_out: '', check_out_location: '', use_assigned_out: false, selectedAssignedOut: '' });
  const [updating, setUpdating] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [userLocation, setUserLocation] = useState(null);
  const [userLocations, setUserLocations] = useState([]);
  const [userPunchingId, setUserPunchingId] = useState('');
  const [editLocOpen, setEditLocOpen] = useState(false);
  const [newUserLocation, setNewUserLocation] = useState({ address: '', lat: 0, lng: 0, location_id: null, userpunchingid: '' });
  const [punchLocations, setPunchLocations] = useState([]); // all reusable locations
  const [editLocMode, setEditLocMode] = useState('custom'); // 'select' = saved, 'custom' = map pin
  const [selectedPunchLoc, setSelectedPunchLoc] = useState(null);

  const [mapDialog, setMapDialog] = useState(null); // { lat, lng, label }

  // Check if both latitude and longitude are within thresholdMeters of reference point.
  // NOTE: 200m matches the mobile app's punch radius (PunchingScreen.js warns the user
  // only when they are farther than 200m from the assigned location). Using a smaller
  // radius here (e.g. 20m) would flag normal GPS drift as "Different from Assigned Location".
  const isWithinRadius = (refLat, refLon, checkLat, checkLon, thresholdMeters = 200) => {
      // Approximate meters per degree at this latitude
      const metersPerDegLat = 111320; // 1 degree latitude ≈ 111.32 km
      const metersPerDegLon = 111320 * Math.cos(refLat * (Math.PI / 180)); // accounts for longitude convergence
      
      const latDiff = Math.abs(checkLat - refLat) * metersPerDegLat;
      const lonDiff = Math.abs(checkLon - refLon) * metersPerDegLon;
      
      const distance = Math.sqrt(latDiff * latDiff + lonDiff * lonDiff);
      return distance <= thresholdMeters;
  }

  const isLocationMatch = (logLocString) => {
      // 1. Check if user has any assigned location
      const locs = userLocations.filter(l => l.latitude && l.longitude);
      if (locs.length === 0) return 'unknown'; // Grey
      
      // 2. Parse log location
      if (!logLocString || typeof logLocString !== 'string') return 'unknown';
      const parts = logLocString.split(',');
      if (parts.length !== 2) return 'unknown'; // Likely an address manually typed
      
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      
      if (isNaN(lat) || isNaN(lng)) return 'unknown';
      
      // 3. Check that the punch is within 200m of ANY assigned location
      return locs.some(l => isWithinRadius(l.latitude, l.longitude, lat, lng, 200)) ? 'match' : 'mismatch';
  }

  const findMatchedLocation = (logLocString) => {
      const locs = userLocations.filter(l => l.latitude && l.longitude);
      if (!logLocString || typeof logLocString !== 'string') return null;
      const parts = logLocString.split(',');
      if (parts.length !== 2) return null;
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (isNaN(lat) || isNaN(lng)) return null;
      return locs.find(l => isWithinRadius(l.latitude, l.longitude, lat, lng, 200)) || null;
  };

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const [res, locRes, punchRes] = await Promise.all([
             axios.get(`${API_URL}/punching/attendance/${id}`, { headers }),
             axios.get(`${API_URL}/punching/locations`, { headers }),
             axios.get(`${API_URL}/punching/punch-locations`, { headers })
        ]);
        setPunchLocations(punchRes.data);
        
        setAttendance(res.data);
        if (res.data.length > 0) {
            setUserName(res.data[0].user_name);
        }

        // Find assigned locations for this user (supports multiple)
        const assignedList = locRes.data.filter(l => l.user_id === parseInt(id));
        setUserLocations(assignedList);
        const assigned = assignedList[0];
        if (assigned) {
            setUserLocation(assigned);
            setUserPunchingId(assigned.userpunchingid || '');
            setNewUserLocation({ address: assigned.address, lat: assigned.latitude, lng: assigned.longitude, userpunchingid: assigned.userpunchingid || '', location_id: assigned.location_id || null });
            if (assigned.location_id) {
                setEditLocMode('select');
                const pl = punchRes.data.find(pl => pl.id === assigned.location_id);
                setSelectedPunchLoc(pl || null);
            }
        }

      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  const handleUpdateLocation = async () => {
      if (!userLocation) return;
      try {
          const payload = {
              address: newUserLocation.address,
              userpunchingid: newUserLocation.userpunchingid
          };
          if (editLocMode === 'select' && selectedPunchLoc) {
              payload.location_id = selectedPunchLoc.id;
          } else {
              payload.location_id = null;
              payload.latitude = newUserLocation.lat;
              payload.longitude = newUserLocation.lng;
          }
          await axios.put(`${API_URL}/punching/locations/${userLocation.id}`, payload, { headers });
          
          // Refresh — reload from server to get effective coordinates
          const locRes = await axios.get(`${API_URL}/punching/locations`, { headers });
          const punchRes = await axios.get(`${API_URL}/punching/punch-locations`, { headers });
          setPunchLocations(punchRes.data);
          const assigned = locRes.data.find(l => l.user_id === parseInt(id));
          if (assigned) {
              setUserLocation(assigned);
              setUserPunchingId(assigned.userpunchingid || '');
              setNewUserLocation({ address: assigned.address, lat: assigned.latitude, lng: assigned.longitude, userpunchingid: assigned.userpunchingid || '', location_id: assigned.location_id || null });
              if (assigned.location_id) {
                  setEditLocMode('select');
                  const pl = punchRes.data.find(pl => pl.id === assigned.location_id);
                  setSelectedPunchLoc(pl || null);
              } else {
                  setEditLocMode('custom');
              }
          }
          setEditLocOpen(false);
          alert('Assigned location updated');
      } catch (e) {
          alert('Error updating location');
      }
  }

  const handleAddLocation = async () => {
      try {
          const payload = {
              user_id: parseInt(id),
              address: newUserLocation.address,
              userpunchingid: newUserLocation.userpunchingid
          };
          if (editLocMode === 'select' && selectedPunchLoc) {
              payload.location_id = selectedPunchLoc.id;
          } else {
              payload.latitude = newUserLocation.lat;
              payload.longitude = newUserLocation.lng;
          }
          await axios.post(`${API_URL}/punching/locations`, payload, { headers });
          setEditLocOpen(false);
          alert('New location added');
          window.location.reload();
      } catch (e) {
          alert('Error adding location');
      }
  };

  const handleDeleteLocation = async (locId) => {
      if (!window.confirm('Delete this location?')) return;
      try {
          await axios.delete(`${API_URL}/punching/locations/${locId}`, { headers });
          setUserLocations(prev => prev.filter(l => l.id !== locId));
          if (userLocation && userLocation.id === locId) setUserLocation(null);
          window.location.reload();
      } catch (e) {
          alert('Error deleting location');
      }
  };

  const assignedLocationFrom = (lat, lng) => {
      if (lat == null || lng == null) return '';
      return `${lat}, ${lng}`;
  };

  const buildDateTime = (date, time) => {
      if (!date || !time) return null;
      return `${date}T${time}:00`;
  };

  const matchedLocationName = (coordStr) => {
      const loc = findMatchedLocation(coordStr);
      return loc ? (loc.punch_location_name || loc.address || '') : '';
  };

  const handleEditClick = (row) => {
      const inMatch = findMatchedLocation(row.check_in_location);
      const outMatch = findMatchedLocation(row.check_out_location);
      setEditData({
          id: row.id,
          date: row.date || '',
          check_in: row.check_in && row.check_in !== '-' ? row.check_in.slice(0, 5) : '',
          check_in_location: row.check_in_location && row.check_in_location !== '-' ? row.check_in_location : '',
          use_assigned_in: !!inMatch,
          selectedAssignedIn: inMatch ? (inMatch.id || '') : '',
          check_out: row.check_out && row.check_out !== '-' ? row.check_out.slice(0, 5) : '',
          check_out_location: row.check_out_location && row.check_out_location !== '-' ? row.check_out_location : '',
          use_assigned_out: !!outMatch,
          selectedAssignedOut: outMatch ? (outMatch.id || '') : ''
      });
      setEditOpen(true);
  };

  const handleCheckInOnSite = (checked) => {
      setEditData(prev => {
          const loc = userLocations[0];
          return {
              ...prev,
              use_assigned_in: checked,
              check_in_location: checked ? assignedLocationFrom(loc?.latitude, loc?.longitude) : '',
              selectedAssignedIn: checked ? (loc?.id || '') : ''
          };
      });
  };

  const handleCheckOutOnSite = (checked) => {
      setEditData(prev => {
          const loc = userLocations[0];
          return {
              ...prev,
              use_assigned_out: checked,
              check_out_location: checked ? assignedLocationFrom(loc?.latitude, loc?.longitude) : '',
              selectedAssignedOut: checked ? (loc?.id || '') : ''
          };
      });
  };

  const handleSelectAssignedIn = (locId) => {
      const loc = userLocations.find(l => l.id === locId);
      setEditData(prev => ({ ...prev, selectedAssignedIn: locId, check_in_location: loc ? assignedLocationFrom(loc.latitude, loc.longitude) : '' }));
  };

  const handleSelectAssignedOut = (locId) => {
      const loc = userLocations.find(l => l.id === locId);
      setEditData(prev => ({ ...prev, selectedAssignedOut: locId, check_out_location: loc ? assignedLocationFrom(loc.latitude, loc.longitude) : '' }));
  };

  const handleRemoveCheckIn = () => {
      setEditData(prev => ({ ...prev, check_in: '', check_in_location: '', use_assigned_in: false }));
  };

  const handleRemoveCheckOut = () => {
      setEditData(prev => ({ ...prev, check_out: '', check_out_location: '', use_assigned_out: false }));
  };

  const handleSaveEdit = async () => {
      setUpdating(true);
      try {
          const payload = {
              check_in: buildDateTime(editData.date, editData.check_in),
              check_in_location: editData.check_in_location || null,
              check_out: buildDateTime(editData.date, editData.check_out),
              check_out_location: editData.check_out_location || null
          };
          await axios.put(`${API_URL}/punching/attendance/${editData.id}`, payload, { headers });
          setAttendance(prev => prev.map(p => p.id === editData.id ? {
              ...p,
              check_in: editData.check_in ? editData.check_in + ':00' : '-',
              check_in_location: editData.check_in_location || '-',
              check_out: editData.check_out ? editData.check_out + ':00' : '-',
              check_out_location: editData.check_out_location || '-'
          } : p));
          setEditOpen(false);
      } catch (err) {
          alert('Failed to update record');
      } finally {
          setUpdating(false);
      }
  };

  const handleOpenAddPunch = () => {
      setAddPunchData({ date: new Date().toISOString().slice(0, 10), check_in: '', check_in_location: '', check_out: '', check_out_location: '' });
      setAddPunchOpen(true);
  };

  const handleAddPunch = async () => {
      if (!addPunchData.date) return alert('Select a date');
      if (!addPunchData.check_in && !addPunchData.check_out) return alert('Provide at least In or Out time');
      setLoading(true);
      try {
          await axios.post(`${API_URL}/punching/attendance/record`, {
              user_id: parseInt(id),
              date: addPunchData.date,
              check_in: addPunchData.check_in ? `${addPunchData.date}T${addPunchData.check_in}:00` : null,
              check_in_location: addPunchData.check_in_location || null,
              check_out: addPunchData.check_out ? `${addPunchData.date}T${addPunchData.check_out}:00` : null,
              check_out_location: addPunchData.check_out_location || null
          }, { headers });
          setAddPunchOpen(false);
          window.location.reload();
      } catch (err) {
          alert('Error adding punch');
      } finally {
          setLoading(false);
      }
  };

  const handleAddCheckInOnSite = (checked) => {
      setAddPunchData(prev => {
          const loc = userLocations[0];
          return { ...prev, use_assigned_in: checked, check_in_location: checked ? assignedLocationFrom(loc?.latitude, loc?.longitude) : '', selectedAssignedIn: checked ? (loc?.id || '') : '' };
      });
  };

  const handleAddCheckOutOnSite = (checked) => {
      setAddPunchData(prev => {
          const loc = userLocations[0];
          return { ...prev, use_assigned_out: checked, check_out_location: checked ? assignedLocationFrom(loc?.latitude, loc?.longitude) : '', selectedAssignedOut: checked ? (loc?.id || '') : '' };
      });
  };

  const handleAddSelectAssignedIn = (locId) => {
      const loc = userLocations.find(l => l.id === locId);
      setAddPunchData(prev => ({ ...prev, selectedAssignedIn: locId, check_in_location: loc ? assignedLocationFrom(loc.latitude, loc.longitude) : '' }));
  };

  const handleAddSelectAssignedOut = (locId) => {
      const loc = userLocations.find(l => l.id === locId);
      setAddPunchData(prev => ({ ...prev, selectedAssignedOut: locId, check_out_location: loc ? assignedLocationFrom(loc.latitude, loc.longitude) : '' }));
  };

  const handleExport = () => {
    const hdrs = ["Date", "In Time", "In Location", "Out Time", "Out Location", "Status"];
    const csvContent = [
        hdrs.join(','),
        ...filteredAttendance.map(row => [
            row.date,
            row.check_in,
            `"${row.check_in_location}"`,
            row.check_out,
            `"${row.check_out_location}"`,
            row.check_out !== '-' ? 'Completed' : 'Active'
        ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Attendance_${userName || 'User'}_${filterFrom || 'start'}_to_${filterTo || 'end'}.csv`;
    a.click();
  };

  const handleOpenMap = (coords, label) => {
    setMapDialog({ lat: coords.lat, lng: coords.lng, label });
  };

  if (loading) return <Loading message="Loading Attendance Records..." />;

  const filteredAttendance = attendance.filter(row => {
    const fromMatch = filterFrom ? row.date >= filterFrom : true;
    const toMatch = filterTo ? row.date <= filterTo : true;
    const statusMatch = filterStatus === 'All' ? true :
        filterStatus === 'Active' ? row.check_out === '-' :
        row.check_out !== '-';
    return fromMatch && toMatch && statusMatch;
  });

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/punching')} sx={{ color: 'text.secondary', fontWeight: 'bold', '&:hover': { bgcolor: '#f1f5f9' } }}>
            Back to Assignments
        </Button>
      </Box>

      {/* Modern Gradient Header */}
      <Grow in={true} timeout={500}>
        <Box sx={{ 
            mb: 4, 
            p: 4, 
            borderRadius: 4, 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 3
        }}>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                    <PersonIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                    {userName || 'User Details'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Attendance Logs & Location Tracking
                    </Typography>
                    {userPunchingId && (
                        <Chip
                            label={`Punching ID: ${userPunchingId}`}
                            size="small"
                            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 'bold' }}
                        />
                    )}
                                                            {userLocations.length > 0 && (
                        <Chip
                            label={`${userLocations.length} Assigned Location${userLocations.length > 1 ? 's' : ''}`}
                            size="small"
                            icon={<LocationOnIcon sx={{ fontSize: 14, color: 'white !important' }} />}
                            onClick={() => setEditLocOpen(true)}
                            sx={{ cursor: 'pointer', bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 'bold', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, '& .MuiChip-icon': { color: 'white' } }}
                        />
                    )}
                </Box>
            </Box>
            {/* Decorative background circles */}
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        </Box>
      </Grow>

      {/* Filters and Controls */}
      <Card elevation={0} sx={{ mb: 4, p: 2, borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'flex-end', alignItems: { xs: 'stretch', md: 'center' }, gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
                <TextField label="From" type="date" size="small" InputLabelProps={{ shrink: true }} value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { backgroundColor: '#f8fafc', borderRadius: 2, '& fieldset': { borderColor: '#e2e8f0' } } }} />
                <TextField label="To" type="date" size="small" InputLabelProps={{ shrink: true }} value={filterTo} onChange={(e) => setFilterTo(e.target.value)} sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { backgroundColor: '#f8fafc', borderRadius: 2, '& fieldset': { borderColor: '#e2e8f0' } } }} />
                <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} size="small" sx={{ minWidth: 140, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                    <MenuItem value="All">All Status</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                </Select>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3, borderColor: '#e2e8f0', color: '#475569', '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' }, height: '40px' }}>Export CSV</Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddPunch} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3, height: '40px' }}>Add Punch</Button>
            </Box>
        </Box>
      </Card>

      {filteredAttendance.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: '#f8fafc' }}>
              <Typography color="text.secondary">No attendance records found for this filter.</Typography>
          </Paper>
      ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', overflowX: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
            <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', py: 2 }}>DATE</TableCell>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', py: 2 }}>IN TIME</TableCell>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', py: 2 }}>IN LOCATION</TableCell>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', py: 2 }}>OUT TIME</TableCell>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', py: 2 }}>OUT LOCATION</TableCell>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', py: 2 }} align="right">ACTIONS</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredAttendance.map((row) => (
                        <TableRow key={row.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell sx={{ fontWeight: '600', color: '#1e293b' }}>{row.date}</TableCell>
                            <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>{row.check_in}</TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {(() => { const m = findMatchedLocation(row.check_in_location); if (m) return <Chip label={m.punch_location_name || m.address || 'Assigned Location'} size="small" color="success" icon={<LocationOnIcon sx={{ fontSize: 14 }} />} sx={{ fontSize: '0.75rem', height: 24 }} />; return <ClickableLocation locationString={row.check_in_location} label="Check-in" onOpenMap={handleOpenMap} />; })()}
                                    {(() => { const s = isLocationMatch(row.check_in_location); if (s === 'match') return <Tooltip title="Matches Assigned Location"><CheckCircleIcon color="success" fontSize="small" /></Tooltip>; if (s === 'mismatch') return <Tooltip title="Different from Assigned Location"><CancelIcon color="error" fontSize="small" /></Tooltip>; return <Tooltip title="Cannot verify location"><HelpIcon color="disabled" fontSize="small" /></Tooltip>; })()}
                                </Box>
                            </TableCell>
                            <TableCell sx={{ color: 'error.main', fontWeight: 'bold' }}>{row.check_out}</TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {(() => { const m = findMatchedLocation(row.check_out_location); if (m) return <Chip label={m.punch_location_name || m.address || 'Assigned Location'} size="small" color="success" icon={<LocationOnIcon sx={{ fontSize: 14 }} />} sx={{ fontSize: '0.75rem', height: 24 }} />; return <ClickableLocation locationString={row.check_out_location} label="Check-out" onOpenMap={handleOpenMap} />; })()}
                                    {(() => { if (!row.check_out_location || row.check_out_location === '-') return null; const s = isLocationMatch(row.check_out_location); if (s === 'match') return <Tooltip title="Matches Assigned Location"><CheckCircleIcon color="success" fontSize="small" /></Tooltip>; if (s === 'mismatch') return <Tooltip title="Different from Assigned Location"><CancelIcon color="error" fontSize="small" /></Tooltip>; return <Tooltip title="Cannot verify location"><HelpIcon color="disabled" fontSize="small" /></Tooltip>; })()}
                                </Box>
                            </TableCell>
                            <TableCell align="right">
                                <IconButton size="small" onClick={() => handleEditClick(row)} sx={{ color: '#2563eb', bgcolor: '#eff6ff', '&:hover': { bgcolor: '#dbeafe' } }}><EditIcon fontSize="small" /></IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
          </TableContainer>
        )}

      {/* Add Punch Dialog */}
      <Dialog open={addPunchOpen} onClose={() => setAddPunchOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' } }}>
          <DialogTitle sx={{ fontWeight: '800', background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white', display: 'flex', alignItems: 'center', gap: 1.5, p: 3 }}>
              <AddIcon /> Add Punch
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3, bgcolor: '#f8fafc' }}>
              <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField label="Date" type="date" fullWidth value={addPunchData.date} onChange={(e) => setAddPunchData({...addPunchData, date: e.target.value})} InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white' }} />
                  <Grid container spacing={2}>
                      <Grid item xs={6}><TextField label="In Time" type="time" fullWidth value={addPunchData.check_in} onChange={(e) => setAddPunchData({...addPunchData, check_in: e.target.value})} InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white' }} /></Grid>
                      <Grid item xs={6}><TextField label="Out Time" type="time" fullWidth value={addPunchData.check_out} onChange={(e) => setAddPunchData({...addPunchData, check_out: e.target.value})} InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white' }} /></Grid>
                  </Grid>
                  <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: 'white' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="overline" color="text.secondary" fontWeight="800">CHECK IN</Typography>
                          <FormControlLabel control={<Switch checked={addPunchData.use_assigned_in} onChange={(e, checked) => handleAddCheckInOnSite(checked)} />} label="On Site" />
                      </Stack>
                      {addPunchData.use_assigned_in && userLocations.length > 0 && (
                          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                              <InputLabel id="add-assigned-in-label">Assigned Location</InputLabel>
                              <Select labelId="add-assigned-in-label" label="Assigned Location" value={addPunchData.selectedAssignedIn || ''} onChange={(e) => handleAddSelectAssignedIn(e.target.value)} sx={{ bgcolor: 'white' }}>
                                  {userLocations.map(loc => (<MenuItem key={loc.id} value={loc.id}>{loc.punch_location_name || loc.address || `Location ${loc.id}`}</MenuItem>))}
                              </Select>
                          </FormControl>
                      )}
                      {!addPunchData.use_assigned_in && (
                          <Box sx={{ height: 200, borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0', mb: 1 }}>
                              <MapContainer center={(() => { const p = parseCoordinates(addPunchData.check_in_location); return p ? [p.lat, p.lng] : [25, 45]; })()} zoom={parseCoordinates(addPunchData.check_in_location) ? 15 : 6} style={{ height: '100%', width: '100%' }}>
                                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                  <LocationMarker position={(() => { const p = parseCoordinates(addPunchData.check_in_location); return p ? { lat: p.lat, lng: p.lng } : null; })()} setPosition={(pos) => setAddPunchData(prev => ({ ...prev, check_in_location: `${pos.lat}, ${pos.lng}` }))} />
                              </MapContainer>
                          </Box>
                      )}
                      <TextField label="In Location" fullWidth value={addPunchData.check_in_location} onChange={(e) => setAddPunchData({...addPunchData, check_in_location: e.target.value})} placeholder="lat, lng" sx={{ bgcolor: 'white' }} />
                  </Box>
                  <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: 'white' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="overline" color="text.secondary" fontWeight="800">CHECK OUT</Typography>
                          <FormControlLabel control={<Switch checked={addPunchData.use_assigned_out} onChange={(e, checked) => handleAddCheckOutOnSite(checked)} />} label="On Site" />
                      </Stack>
                      {addPunchData.use_assigned_out && userLocations.length > 0 && (
                          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                              <InputLabel id="add-assigned-out-label">Assigned Location</InputLabel>
                              <Select labelId="add-assigned-out-label" label="Assigned Location" value={addPunchData.selectedAssignedOut || ''} onChange={(e) => handleAddSelectAssignedOut(e.target.value)} sx={{ bgcolor: 'white' }}>
                                  {userLocations.map(loc => (<MenuItem key={loc.id} value={loc.id}>{loc.punch_location_name || loc.address || `Location ${loc.id}`}</MenuItem>))}
                              </Select>
                          </FormControl>
                      )}
                      {!addPunchData.use_assigned_out && (
                          <Box sx={{ height: 200, borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0', mb: 1 }}>
                              <MapContainer center={(() => { const p = parseCoordinates(addPunchData.check_out_location); return p ? [p.lat, p.lng] : [25, 45]; })()} zoom={parseCoordinates(addPunchData.check_out_location) ? 15 : 6} style={{ height: '100%', width: '100%' }}>
                                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                  <LocationMarker position={(() => { const p = parseCoordinates(addPunchData.check_out_location); return p ? { lat: p.lat, lng: p.lng } : null; })()} setPosition={(pos) => setAddPunchData(prev => ({ ...prev, check_out_location: `${pos.lat}, ${pos.lng}` }))} />
                              </MapContainer>
                          </Box>
                      )}
                      <TextField label="Out Location" fullWidth value={addPunchData.check_out_location} onChange={(e) => setAddPunchData({...addPunchData, check_out_location: e.target.value})} placeholder="lat, lng" sx={{ bgcolor: 'white' }} />
                  </Box>
              </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
              <Button onClick={() => setAddPunchOpen(false)} sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Cancel</Button>
              <Button variant="contained" onClick={handleAddPunch} disabled={loading} sx={{ borderRadius: 2, px: 3, fontWeight: 'bold' }}>{loading ? 'Saving...' : 'Save'}</Button>
          </DialogActions>
      </Dialog>

      {/* Edit Punch Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' } }}>
          <DialogTitle sx={{ fontWeight: '800', background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white', display: 'flex', alignItems: 'center', gap: 1.5, p: 3 }}>
              <EditIcon /> Edit Punch
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3, bgcolor: '#f8fafc' }}>
              <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: 'white', height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="overline" color="text.secondary" fontWeight="800">CHECK IN</Typography>
                              <FormControlLabel control={<Switch checked={editData.use_assigned_in} onChange={(e, checked) => handleCheckInOnSite(checked)} />} label="On Site" />
                          </Stack>
                          <TextField label="In Time" type="time" fullWidth value={editData.check_in} onChange={(e) => setEditData({...editData, check_in: e.target.value})} InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white' }} />
                          {editData.use_assigned_in && userLocations.length > 0 && (
                              <FormControl fullWidth size="small">
                                  <InputLabel id="edit-assigned-in-label">Assigned Location</InputLabel>
                                  <Select labelId="edit-assigned-in-label" label="Assigned Location" value={editData.selectedAssignedIn || ''} onChange={(e) => handleSelectAssignedIn(e.target.value)} sx={{ bgcolor: 'white' }}>
                                      {userLocations.map(loc => (<MenuItem key={loc.id} value={loc.id}>{loc.punch_location_name || loc.address || `Location ${loc.id}`}</MenuItem>))}
                                  </Select>
                              </FormControl>
                          )}
                          {!editData.use_assigned_in && (
                              <TextField label="In Location" fullWidth value={editData.check_in_location} onChange={(e) => setEditData({...editData, check_in_location: e.target.value})} placeholder="lat, lng" sx={{ bgcolor: 'white' }} InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => { const c = parseCoordinates(editData.check_in_location); if (c) handleOpenMap(c, 'Check-in'); }}><LocationOnIcon fontSize="small" /></IconButton></InputAdornment> }} />
                          )}
                          {editData.check_in && <Button size="small" color="error" startIcon={<DeleteIcon fontSize="small" />} onClick={handleRemoveCheckIn} sx={{ alignSelf: 'flex-start' }}>Remove</Button>}
                      </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: 'white', height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="overline" color="text.secondary" fontWeight="800">CHECK OUT</Typography>
                              <FormControlLabel control={<Switch checked={editData.use_assigned_out} onChange={(e, checked) => handleCheckOutOnSite(checked)} />} label="On Site" />
                          </Stack>
                          <TextField label="Out Time" type="time" fullWidth value={editData.check_out} onChange={(e) => setEditData({...editData, check_out: e.target.value})} InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white' }} />
                          {editData.use_assigned_out && userLocations.length > 0 && (
                              <FormControl fullWidth size="small">
                                  <InputLabel id="edit-assigned-out-label">Assigned Location</InputLabel>
                                  <Select labelId="edit-assigned-out-label" label="Assigned Location" value={editData.selectedAssignedOut || ''} onChange={(e) => handleSelectAssignedOut(e.target.value)} sx={{ bgcolor: 'white' }}>
                                      {userLocations.map(loc => (<MenuItem key={loc.id} value={loc.id}>{loc.punch_location_name || loc.address || `Location ${loc.id}`}</MenuItem>))}
                                  </Select>
                              </FormControl>
                          )}
                          {!editData.use_assigned_out && (
                              <TextField label="Out Location" fullWidth value={editData.check_out_location} onChange={(e) => setEditData({...editData, check_out_location: e.target.value})} placeholder="lat, lng" sx={{ bgcolor: 'white' }} InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => { const c = parseCoordinates(editData.check_out_location); if (c) handleOpenMap(c, 'Check-out'); }}><LocationOnIcon fontSize="small" /></IconButton></InputAdornment> }} />
                          )}
                          {editData.check_out && <Button size="small" color="error" startIcon={<DeleteIcon fontSize="small" />} onClick={handleRemoveCheckOut} sx={{ alignSelf: 'flex-start' }}>Remove</Button>}
                      </Box>
                  </Grid>
              </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
              <Button onClick={() => setEditOpen(false)} sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveEdit} disabled={updating} sx={{ borderRadius: 2, px: 3, fontWeight: 'bold' }}>{updating ? 'Saving...' : 'Save Changes'}</Button>
          </DialogActions>
      </Dialog>

      {/* Edit Assigned Location Dialog */}
      <Dialog 
          open={editLocOpen} 
          onClose={() => setEditLocOpen(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{ sx: { borderRadius: 4, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' } }}
      >
          <DialogTitle sx={{ 
              fontWeight: '800', 
              background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
              color: 'white',
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5,
              p: 3
          }}>
              <LocationOnIcon /> Update Assigned Location
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3, bgcolor: '#f8fafc' }}>
             {userLocations.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>CURRENT LOCATIONS</Typography>
                    <Stack spacing={1}>
                        {userLocations.map(loc => (
                            <Box key={loc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: 'white' }}>
                                <LocationOnIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                                <Typography variant="body2" fontWeight="600" sx={{ flexGrow: 1 }}>{loc.punch_location_name || loc.address || 'Assigned Location'}</Typography>
                                <IconButton size="small" sx={{ color: '#ef4444' }} onClick={() => handleDeleteLocation(loc.id)}><DeleteIcon fontSize="small" /></IconButton>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}
             <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>PUNCHING ID</Typography>
                        <TextField
                            label="User Punching ID"
                            fullWidth
                            value={newUserLocation.userpunchingid || ''}
                            onChange={e => setNewUserLocation({...newUserLocation, userpunchingid: e.target.value})}
                            placeholder="e.g. EMP-001"
                            sx={{ bgcolor: 'white', mb: 2 }}
                        />
                    </Box>
                    
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>LOCATION MODE</Typography>
                        <Tabs
                          value={editLocMode}
                          onChange={(e, v) => { setEditLocMode(v); setSelectedPunchLoc(null); }}
                          sx={{ mb: 2, minHeight: 'auto', '& .MuiTab-root': { minHeight: 'auto', py: 1, px: 1.5, fontSize: '0.75rem', fontWeight: 'bold' } }}
                        >
                          <Tab label="Saved Location" value="select" />
                          <Tab label="Custom Pin" value="custom" />
                        </Tabs>

                        {editLocMode === 'select' ? (
                          <Autocomplete
                              options={punchLocations}
                              getOptionLabel={(opt) => `${opt.name} (${opt.latitude?.toFixed(4)}, ${opt.longitude?.toFixed(4)})`}
                              value={selectedPunchLoc}
                              onChange={(e, v) => {
                                  setSelectedPunchLoc(v);
                                  if (v) {
                                      setNewUserLocation(prev => ({
                                          ...prev,
                                          lat: v.latitude,
                                          lng: v.longitude,
                                          location_id: v.id,
                                          address: v.address || v.name
                                      }));
                                  }
                              }}
                              renderInput={(params) => <TextField {...params} label="Select Location" fullWidth sx={{ bgcolor: 'white' }} />}
                          />
                        ) : (
                          <Box sx={{ p: 2, bgcolor: '#eff6ff', borderRadius: 2, border: '1px solid #bfdbfe', mb: 2 }}>
                              <Typography variant="caption" color="#2563eb" fontWeight="bold" display="block">SELECTED COORDINATES</Typography>
                              <Typography variant="body2" fontWeight="800" sx={{ mt: 0.5, color: '#1e3a8a' }}>{newUserLocation.lat?.toFixed(6) ?? 0}, {newUserLocation.lng?.toFixed(6) ?? 0}</Typography>
                              <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#3b82f6' }}>
                                  Click on the map to drop a pin.
                              </Typography>
                          </Box>
                        )}
                    </Box>
                    
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>ADDRESS (optional)</Typography>
                        <TextField 
                            label="Address" 
                            fullWidth 
                            value={newUserLocation.address} 
                            onChange={e => setNewUserLocation({...newUserLocation, address: e.target.value})}
                            sx={{ bgcolor: 'white' }}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>MAP {editLocMode === 'select' && selectedPunchLoc ? '(preview)' : 'PIN'}</Typography>
                    <Box sx={{ height: 400, borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0', width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <MapContainer 
                            center={
                                editLocMode === 'select' && selectedPunchLoc 
                                    ? [selectedPunchLoc.latitude, selectedPunchLoc.longitude] 
                                    : newUserLocation.lat ? [newUserLocation.lat, newUserLocation.lng] : [25, 45]
                            } 
                            zoom={editLocMode === 'select' && selectedPunchLoc ? 15 : 10} 
                            style={{ height: '100%', width: '100%' }}
                        >
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            {editLocMode === 'select' && selectedPunchLoc ? (
                              <Marker position={[selectedPunchLoc.latitude, selectedPunchLoc.longitude]}>
                                <Popup>{selectedPunchLoc.name}</Popup>
                              </Marker>
                            ) : (
                              <LocationMarker 
                                  position={newUserLocation.lat ? { lat: newUserLocation.lat, lng: newUserLocation.lng } : null} 
                                  setPosition={(pos) => setNewUserLocation(prev => ({ ...prev, lat: pos.lat, lng: pos.lng }))} 
                              />
                            )}
                        </MapContainer>
                    </Box>
                </Grid>
             </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
               <Button onClick={() => setEditLocOpen(false)} sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Cancel</Button>
               <Button variant="outlined" onClick={handleUpdateLocation} sx={{ borderRadius: 2, px: 3, fontWeight: 'bold', color: '#475569', borderColor: '#cbd5e1' }}>Update First Location</Button>
               <Button variant="contained" onClick={handleAddLocation} sx={{ borderRadius: 2, px: 3, fontWeight: 'bold', bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}>Add Location</Button>
          </DialogActions>
      </Dialog>

      {/* Map Preview Dialog */}
      <Dialog
          open={!!mapDialog}
          onClose={() => setMapDialog(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 4, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' } }}
      >
          <DialogTitle sx={{
              fontWeight: '800',
              background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 3
          }}>
              <LocationOnIcon /> Location on Map
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3, bgcolor: '#f8fafc' }}>
              {mapDialog && (
                  <>
                      <Typography variant="subtitle1" fontWeight="800" sx={{ mb: 1, color: '#1e3a8a' }}>
                          {mapDialog.label}: {mapDialog.lat.toFixed(6)}, {mapDialog.lng.toFixed(6)}
                      </Typography>
                      <Box sx={{ height: 400, borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0', width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                          <MapContainer
                              center={[mapDialog.lat, mapDialog.lng]}
                              zoom={15}
                              style={{ height: '100%', width: '100%' }}
                          >
                              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                              <Marker position={[mapDialog.lat, mapDialog.lng]}>
                                  <Popup>
                                      {mapDialog.label}<br />{mapDialog.lat.toFixed(6)}, {mapDialog.lng.toFixed(6)}
                                  </Popup>
                              </Marker>
                              <MapResizer />
                          </MapContainer>
                      </Box>
                      <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                              variant="outlined"
                              startIcon={<LocationOnIcon />}
                              onClick={() => window.open(`https://www.google.com/maps?q=${mapDialog.lat},${mapDialog.lng}`, '_blank', 'noopener,noreferrer')}
                              sx={{ borderRadius: 2, fontWeight: 'bold' }}
                          >
                              Open in Google Maps
                          </Button>
                      </Box>
                  </>
              )}
          </DialogContent>
          <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setMapDialog(null)} sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Close</Button>
          </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PunchingDetails;
